import { Link } from "react-router-dom";
import { ArrowLeftFromLine, ArrowRightToLine, FileText, Receipt } from "lucide-react";
import { cn } from "@/lib/utils";
import type { JobDetail, LinkedEstimate, LinkedInvoice } from "./types";

/**
 * Paperwork lineage, pinned at the top of the job (Corbin's ask: "Invoice and
 * Estimate links should also be at the top … jobs created from estimates and
 * jobs that failed that created estimates, links forward and backwards").
 *
 * One compact row of clickable chips that reads the job's money trail both ways:
 *   ← the estimate this job was created FROM (source_estimate_*), and
 *   → the estimate(s) and invoice(s) this job PRODUCED (linkedEstimates /
 *     linkedInvoices — this is also where a failed test's auto-drafted repair
 *     quote shows up).
 *
 * Purely presentational over data the job page already fetches — no new
 * endpoint. Renders nothing when the job has no paperwork either way.
 */

type SourceEstimate = Pick<
    JobDetail,
    | "source_estimate_id"
    | "source_estimate_display_number"
    | "source_estimate_status"
    | "source_estimate_approved_by"
    | "source_estimate_approved_at"
>;

function money(amount: number | string | null | undefined): string | null {
    if (amount == null) return null;
    const n = Number(amount);
    if (!Number.isFinite(n)) return null;
    return `$${Math.round(n).toLocaleString()}`;
}

/** Shared chip look; `dir` picks the lead arrow (came-from vs produced). */
function LineageChip({
    to,
    dir,
    icon: Icon,
    title,
    children,
}: {
    to: string;
    dir: "from" | "to";
    icon: typeof Receipt;
    title?: string;
    children: React.ReactNode;
}) {
    const Arrow = dir === "from" ? ArrowLeftFromLine : ArrowRightToLine;
    return (
        <Link
            to={to}
            title={title}
            className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs",
                "hover:border-primary/40 hover:bg-primary/5 hover:text-primary transition-colors",
            )}
        >
            <Arrow className="h-3 w-3 shrink-0 text-foreground/45" aria-hidden="true" />
            <Icon className="h-3 w-3 shrink-0 text-foreground/60" aria-hidden="true" />
            {children}
        </Link>
    );
}

export function JobLineageStrip({
    job,
    invoices,
    estimates,
}: {
    job: SourceEstimate;
    invoices: LinkedInvoice[];
    estimates: LinkedEstimate[];
}) {
    const hasSource = !!job.source_estimate_id;
    if (!hasSource && invoices.length === 0 && estimates.length === 0) return null;

    const approvalNote = job.source_estimate_approved_by
        ? `approved by ${job.source_estimate_approved_by}`
        : job.source_estimate_approved_at
          ? "customer approved"
          : job.source_estimate_status
            ? job.source_estimate_status
            : null;

    return (
        <div
            className="mt-1.5 flex flex-wrap items-center gap-1.5"
            aria-label="Estimate and invoice lineage"
            data-testid="job-lineage-strip"
        >
            {hasSource && (
                <LineageChip
                    to={`/commercial/${job.source_estimate_id}?type=estimate`}
                    dir="from"
                    icon={FileText}
                    title={
                        job.source_estimate_approved_at
                            ? `Created from this estimate. Approved${
                                  job.source_estimate_approved_by ? ` by ${job.source_estimate_approved_by}` : ""
                              } on ${new Date(job.source_estimate_approved_at).toLocaleDateString()}.`
                            : "The estimate this job was created from."
                    }
                >
                    <span>
                        From Estimate
                        {job.source_estimate_display_number ? ` #${job.source_estimate_display_number}` : ""}
                    </span>
                    {approvalNote && <span className="text-foreground/55">· {approvalNote}</span>}
                </LineageChip>
            )}

            {estimates.map((est) => (
                <LineageChip
                    key={est.id}
                    to={`/commercial/${est.id}?type=estimate`}
                    dir="to"
                    icon={FileText}
                    title={est.title || undefined}
                >
                    <span>Estimate{est.display_number ? ` #${est.display_number}` : ""}</span>
                    {est.status && <span className="text-foreground/55">· {est.status}</span>}
                </LineageChip>
            ))}

            {invoices.map((inv) => {
                const amt = money(inv.total_amount ?? inv.subtotal);
                return (
                    <LineageChip
                        key={inv.id}
                        to={`/commercial/${inv.id}?type=invoice`}
                        dir="to"
                        icon={Receipt}
                        title={inv.title || undefined}
                    >
                        <span>Invoice{inv.invoice_number ? ` #${inv.invoice_number}` : ""}</span>
                        {amt && <span className="text-foreground/70">· {amt}</span>}
                        {inv.status && <span className="text-foreground/55">· {inv.status}</span>}
                    </LineageChip>
                );
            })}
        </div>
    );
}
